import * as duckdb from '@duckdb/duckdb-wasm';
import { AsyncDuckDB, DuckDBConfig, LogLevel } from '@duckdb/duckdb-wasm';

let DB: Promise<AsyncDuckDB> | undefined;

/**
 * Initialize DuckDB, ensuring we only initialize it once.
 *
 * @param config An optional DuckDBConfig object.
 */
export default async function initializeDuckDb(options?: {
  config?: DuckDBConfig;
}): Promise<AsyncDuckDB> {
  const { config } = options || {};

  if (DB === undefined) {
    DB = _initializeDuckDb(config);
  }
  return DB;
}

/**
 * Initialize DuckDB with a browser-specific Wasm bundle.
 */
const _initializeDuckDb = async (
  config?: DuckDBConfig
): Promise<AsyncDuckDB> => {
  const start = performance.now();

  // Select a bundle based on browser checks
  const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
  const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);

  if (!bundle.mainWorker) {
    throw new Error('MainWorker is not defined in the selected bundle.');
  }
  const worker_url = URL.createObjectURL(
    new Blob([`importScripts("${bundle.mainWorker}");`], {
      type: 'text/javascript',
    })
  );

  // Instantiate the async version of DuckDB-wasm
  const worker = new Worker(worker_url);
  const logger = new duckdb.ConsoleLogger(LogLevel.WARNING);
  const db = new AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule, bundle.pthreadWorker);
  URL.revokeObjectURL(worker_url);

  if (config) {
    await db.open(config);
  }

  console.log(
    'DuckDB initialized',
    `Time spent: ${(performance.now() - start).toFixed(0)}`
  );
  return db;
};

/**
 * Get the instance of DuckDB, initializing it if needed.
 *
 * Typically `useDuckDB` is used in React components instead, but this
 * method provides access outside of React contexts.
 */
export const getDuckDB = async (): Promise<AsyncDuckDB> => {
  if (DB) {
    return DB;
  } else {
    return await initializeDuckDb();
  }
};
